import type { ReactNode } from 'react';
import Icon from 'components/common/Icon';
import { DRAG_BAR_HEIGHT_REM } from 'components/common/BottomSheet';
import { createContext, useContext } from 'react';
import useBottomSheet from 'hooks/common/useBottomSheet';
import ToastBridge from 'bridges/toastBridge';
import { useEffect, useState } from 'react';
import { getUserLocalTime, getFlagUrl } from 'utils/userUtils';

const ProfileContext = createContext<{ userInfo: UserInfo; isTeamLinkle?: boolean } | undefined>(undefined);
export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) throw new Error('프로필 컴포넌트 내에서 사용해야 합니다.');
  return context;
};

const Profile = ({ userInfo, children, isTeamLinkle }: { userInfo: UserInfo; children: ReactNode; isTeamLinkle?: boolean }) => {
  if (!userInfo) return null;
  return (
    <ProfileContext.Provider value={{ userInfo, isTeamLinkle }}>
      <div className="relative h-full w-full">{children}</div>
    </ProfileContext.Provider>
  );
};

const ProfileImage = ({ children }: { children?: ReactNode }) => {
  const { userInfo, isTeamLinkle } = useProfile();
  const [imgUrl, setImgUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!userInfo) return;

    if (isTeamLinkle) {
      setImgUrl('imgweb/profile/linkle-profile.webp');
    } else {
      const lowResUrl = PhotoManager.getPhotoURL(userInfo, false);
      const highResUrl = PhotoManager.getPhotoURL(userInfo, true);

      setImgUrl(lowResUrl);

      const img = new Image();
      img.onload = () => {
        setImgUrl(highResUrl);
      };
      img.src = highResUrl;
    }
  }, [userInfo, isTeamLinkle]);

  return (
    <div data-component="ProfileImage" className={`h-[100dvw] w-[100dvw] overflow-hidden rounded-lg`} style={{ marginTop: `-${DRAG_BAR_HEIGHT_REM}rem`, marginInline: '-1rem' }}>
      <div className="relative w-full" style={{ height: '100dvw' }}>
        {imgUrl && <img alt="" className="h-full w-full object-cover" src={imgUrl} />}
        <div className="absolute bottom-0 left-0 right-0 h-[25%] w-full" style={{ background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.50) 100%)' }} />
        <div className="absolute left-0 right-0 top-0 h-[25%] w-full" style={{ background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.00) 100%)' }} />
        <div className="absolute right-2 top-4">{children}</div>
      </div>
    </div>
  );
};

const Name = () => {
  const { userInfo, isTeamLinkle } = useProfile();

  return (
    <div className="flex items-center gap-1">
      {isTeamLinkle && <Icon name="profile/icon_container.svg" size={24} />}
      {!isTeamLinkle && userInfo.gender === 'female' && <Icon name="profile/female.svg" size={24} />}
      {!isTeamLinkle && userInfo.gender === 'male' && <Icon name="profile/male.svg" size={24} />}
      <div className="typography-headline-lg text-white">{userInfo.nick}</div>
    </div>
  );
};

const Uid = () => {
  const { userInfo } = useProfile();
  const copyToClipboard = () => {
    navigator.clipboard.writeText(userInfo.fbUid);
    ToastBridge.add({
      text: brsc.copied_clipboard,
      options: {
        duration: 2000,
        position: 'top',
        type: 'default',
      },
    });
  };

  return (
    <div className="flex w-fit items-center gap-1 rounded-md bg-base-bg-subtle px-2 py-1" onClick={copyToClipboard}>
      <div className="typography-caption-head text-base-txt-primary">UID</div>
      <p className="typography-caption-body m-0 text-base-txt-primary">{userInfo.fbUid}</p>
      <Icon name="profile/ic_copy.svg" color="filter-base-ic-white" size={24} />
    </div>
  );
};

const Country = () => {
  const { userInfo } = useProfile();
  const location = getCountryName(userInfo.country);

  return (
    <div className="flex w-fit items-center gap-1 rounded-lg bg-base-bg-subtle px-2 py-1">
      <img className="h-6 w-6" alt="" src={getFlagUrl(userInfo.country)} />
      <div className="typography-body-sm text-center text-base-txt-primary">{location}</div>
      {userInfo.locale && <div>{'\u2022'}</div>}
      {userInfo.locale && <div className="typography-body-sm text-center text-base-txt-primary">{brsc[`lang_${userInfo.locale.split('-')[0]}`]}</div>}
    </div>
  );
};

const TimeInfo = () => {
  const { userInfo } = useProfile();
  const { dayOrNight, timeString } = getUserLocalTime(userInfo.tzoffset);

  return (
    <div className="typography-body-sm flex w-fit items-center gap-1 rounded-lg bg-base-bg-subtle px-2 py-1">
      <img className="w-6" alt="" src={`/imgweb/chat/ic_${dayOrNight}.svg`} />
      <div className="">{timeString}</div>
    </div>
  );
};

const Coin = ({ coin, type }: { coin: number | null; type?: 'user' | 'celeb' | 'loveMatch' | 'my' }) => {
  if (coin === null) return null;

  return (
    <>
      {(type === 'user' || type === 'my') && (
        <div className="flex w-fit items-center justify-center gap-[2px] rounded-full bg-base-bg-strong p-1 pr-2">
          <img alt="" src={'imgweb/icons/ic_coin.png'} className="aspect-square h-4 w-4 p-[1.333px]" />
          <div className="typography-caption-body text-base-txt-primary">{coin}</div>
        </div>
      )}
      {type === 'loveMatch' && (
        <div className="flex w-fit items-center justify-center gap-[2px] rounded-full bg-base-bg-strong p-1 pr-2">
          <img alt="" src={'imgweb/icons/ic_coin.png'} className="aspect-square h-4 w-4 p-[1.333px]" />
          <div className="typography-caption-body text-base-txt-primary">{`${coin} / 10sec`}</div>
        </div>
      )}
    </>
  );
};

const Stats = ({ type, count }: { type: 'following' | 'follower'; count: number }) => {
  if (count == null) return null;
  const text = type === 'following' ? brsc.following : brsc.followers;
  const countText = count >= 1000 ? `${Number((count / 1000).toFixed(1))}K` : count;

  return (
    <div className="align-center flex gap-2">
      <div className="typography-body-sm text-base-txt-primary">{text}</div>
      <div className="typography-headline-sm text-base-txt-primary">{countText}</div>
    </div>
  );
};

const BlockUserButton = () => {
  const { open } = useBottomSheet();
  const { userInfo } = useProfile();

  return (
    <div className="flex items-center justify-center">
      <Icon name="profile/report.svg" size={24} onClick={() => open('BlockReportMenu', { id: 'BlockReportMenu', data: { userInfo: userInfo } })} />
    </div>
  );
};

Profile.Image = ProfileImage;
Profile.Name = Name;
Profile.Uid = Uid;
Profile.Country = Country;
Profile.TimeInfo = TimeInfo;
Profile.Coin = Coin;
Profile.Stats = Stats;
Profile.BlockUserButton = BlockUserButton;
export default Profile;
